/**
 * NotificationBell - In-app notification bell with dropdown, toast alerts
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Bell, CheckCheck, Trash2, X,
  AlertTriangle, ClipboardList, User, CalendarDays,
  FileText, FileSignature, Landmark, ScrollText, ListTodo, FolderOpen,
} from "lucide-react";
import { BACKEND_URL, API_V1} from "../config/api";
import { useWS } from "../contexts/WebSocketContext";
import { buildEntityLink } from "../utils/entityLinks";

const API = `${BACKEND_URL}/api/v1/notifications`;

const TYPE_CONFIG = {
  assignment: { icon: User, color: "text-violet-600", bg: "bg-violet-50" },
  info: { icon: Bell, color: "text-blue-600", bg: "bg-blue-50" },
  warning: { icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50" },
  revision: { icon: ClipboardList, color: "text-emerald-600", bg: "bg-emerald-50" },
  status_change: { icon: FileSignature, color: "text-indigo-600", bg: "bg-indigo-50" },
  calendar_invite: { icon: CalendarDays, color: "text-blue-600", bg: "bg-blue-50" },
  calendar_team: { icon: CalendarDays, color: "text-cyan-600", bg: "bg-cyan-50" },
  document: { icon: FileText, color: "text-indigo-600", bg: "bg-indigo-50" },
  task: { icon: ListTodo, color: "text-pink-600", bg: "bg-pink-50" },
  protocol: { icon: ScrollText, color: "text-lime-600", bg: "bg-lime-50" },
  contract: { icon: FileSignature, color: "text-rose-600", bg: "bg-rose-50" },
  subsidy: { icon: Landmark, color: "text-amber-600", bg: "bg-amber-50" },
  file: { icon: FolderOpen, color: "text-teal-600", bg: "bg-teal-50" },
  quote: { icon: FileText, color: "text-emerald-600", bg: "bg-emerald-50" },
};

const ENTITY_PATHS = {
  calendar: "/admin/calendar",
  document: "/admin/documents",
  task: "/admin/tasks",
  protocol: "/admin/protocols",
  contract: "/admin/contracts",
  subsidy: "/admin/subsidies",
  file: "/admin/files",
  quote: "/admin/quotes",
  invoice: "/admin/invoices",
  project: "/admin/projects",
  claim: "/admin/claims",
  service: "/admin/service",
  service_order: "/admin/service",
  rental: "/admin/rentals",
  customer: "/admin/customers",
  submission: "/admin/submissions",
  revision: "/admin/revize",
  installation: "/admin/installations",
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const prevUnreadRef = useRef(0);
  const dropdownRef = useRef(null);

  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(() => { setOpen(false); setClosing(false); }, 200);
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(API, { withCredentials: true });
      const newNotifs = r.data.notifications || [];
      const newUnread = r.data.unread_count || 0;

      // Show toast for NEW unread notifications
      if (newUnread > prevUnreadRef.current && prevUnreadRef.current >= 0) {
        const diff = newUnread - prevUnreadRef.current;
        const latest = newNotifs.find(n => !n.read);
        if (latest && diff > 0) {
          const cfg = TYPE_CONFIG[latest.type] || TYPE_CONFIG.info;
          toast(latest.title, {
            description: latest.message,
            icon: <Bell className={`w-4 h-4 ${cfg.color}`} />,
            duration: 5000,
            action: latest.entity_type && ENTITY_PATHS[latest.entity_type] ? {
              label: "Zobrazit",
              onClick: () => navigate(buildEntityLink(latest.entity_type, latest.entity_id) || ENTITY_PATHS[latest.entity_type]),
            } : undefined,
          });
        }
      }

      prevUnreadRef.current = newUnread;
      setNotifications(newNotifs);
      setUnreadCount(newUnread);
    } catch {}
  }, [navigate]);

  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, [load]);

  // Real-time: refresh on WS notification event
  const ws = useWS();
  useEffect(() => {
    if (!ws) return;
    return ws.subscribe("notifications", () => load());
  }, [ws, load]);

  const markRead = async (id) => {
    await axios.put(`${API_V1}/notifications/${id}/read`, {}, { withCredentials: true }).catch(() => {});
    setNotifications(prev => prev.map(n => n.notification_id === id ? { ...n, read: true } : n));
    setUnreadCount(c => { const v = Math.max(0, c - 1); prevUnreadRef.current = v; return v; });
  };

  const markAllRead = async () => {
    await axios.put(`${API_V1}/notifications/read-all`, {}, { withCredentials: true }).catch(() => {});
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    prevUnreadRef.current = 0;
  };

  const deleteNotif = async (id) => {
    await axios.delete(`${API_V1}/notifications/${id}`, { withCredentials: true }).catch(() => {});
    setNotifications(prev => prev.filter(n => n.notification_id !== id));
    setUnreadCount(c => { const v = Math.max(0, c - 1); prevUnreadRef.current = v; return v; });
  };

  const handleClick = (n) => {
    if (!n.read) markRead(n.notification_id);
    const url = buildEntityLink(n.entity_type, n.entity_id);
    if (url) { handleClose(); navigate(url); }
  };

  const timeAgo = (iso) => {
    if (!iso) return "";
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "právě teď";
    if (diff < 3600) return `před ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `před ${Math.floor(diff / 3600)} h`;
    return `před ${Math.floor(diff / 86400)} d`;
  };

  return (
    <div className="relative">
      <button onClick={() => { if (open) handleClose(); else setOpen(true); }}
        className="relative p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100/80 transition-all duration-200"
        data-testid="notification-bell">
        <Bell className="w-4 h-4" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center animate-pulse" data-testid="notification-badge">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={handleClose} />
          <div ref={dropdownRef}
            className={`absolute right-0 top-full mt-2 w-96 bg-white/95 backdrop-blur-xl border border-slate-200/60 rounded-2xl shadow-2xl z-50 overflow-hidden origin-top-right transition-all duration-200 ease-out ${closing ? "opacity-0 scale-95 translate-y-1" : "opacity-100 scale-100 translate-y-0 animate-notif-dropdown-in"}`}
            data-testid="notification-dropdown">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100/80 bg-slate-50/50">
              <h3 className="text-sm font-bold text-slate-800">Notifikace {unreadCount > 0 && <span className="text-xs font-normal text-slate-400 ml-1">({unreadCount} nepřečtených)</span>}</h3>
              <div className="flex gap-1">
                {unreadCount > 0 && (
                  <button onClick={markAllRead} className="text-[10px] text-emerald-600 hover:text-emerald-700 px-2 py-1 rounded-full hover:bg-emerald-50 transition-colors" data-testid="mark-all-read-btn">
                    <CheckCheck className="w-3 h-3 inline mr-0.5" /> Přečíst vše
                  </button>
                )}
                <button onClick={handleClose} className="text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-100 transition-colors">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto divide-y divide-slate-50">
              {notifications.length === 0 ? (
                <div className="text-center py-10 text-slate-400">
                  <Bell className="w-6 h-6 mx-auto mb-2 opacity-30" />
                  <p className="text-xs">Žádné notifikace</p>
                </div>
              ) : (
                notifications.map((n, idx) => {
                  const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.info;
                  const Icon = cfg.icon;
                  return (
                    <div key={n.notification_id}
                      className={`group flex gap-3 px-4 py-3 hover:bg-emerald-50/30 transition-all duration-150 cursor-pointer notif-item-enter ${!n.read ? "bg-blue-50/30" : ""}`}
                      style={{ animationDelay: `${Math.min(idx * 40, 240)}ms` }}
                      onClick={() => handleClick(n)}
                      data-testid={`notif-${n.notification_id}`}>
                      <div className={`w-8 h-8 rounded-xl ${cfg.bg} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-4 h-4 ${cfg.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          {!n.read && <div className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />}
                          <p className={`text-xs font-medium truncate ${!n.read ? "text-slate-900" : "text-slate-600"}`}>{n.title}</p>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{n.message}</p>
                        <p className="text-[10px] text-slate-400 mt-1">{timeAgo(n.created_at)}</p>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); deleteNotif(n.notification_id); }}
                        className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500 p-1 self-start flex-shrink-0 transition-all duration-200">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
